/**
 * DYNAMIC TROLLEY SYSTEM - WORKING VERSION WITH FIXES
 * Based on your existing working code with alignment improvements
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const NOMINAL_TROLLEY_WIDTH_UNITS = 127;
const BASE_UNIT_SIZE = 5;
const MAX_COLUMNS = Math.max(1, Math.round(NOMINAL_TROLLEY_WIDTH_UNITS / BASE_UNIT_SIZE));
const trolleySpecs = { trolley_height: 190, trolley_length: 127, trolley_base_width: 58 };

function toNumber(value) {
  if (value === null || value === undefined) return NaN;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : NaN;
}

function getColspanByWidth(width) {
  return Math.max(1, Math.round(width / BASE_UNIT_SIZE));
}

function getProductMetaByParentId(parentId) {
  if (!Array.isArray(window.productMeta)) return null;
  return window.productMeta.find((p) => parseInt(p.id, 10) === parseInt(parentId, 10)) || null;
}

function parseBooleanFlag(value) {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  return ['true', '1', 'yes', 'y'].includes(normalized);
}

function fetchCartItems(data = []) {
  const source = Array.isArray(data) ? data : [];
  const trolleyItems = [];
  const separateShippingMap = new Map();

  source.forEach((item) => {
    const qty = Math.floor(toNumber(item.qty ?? item.quantity));
    if (!Number.isFinite(qty) || qty <= 0) return;

    const metafields = item.metafields || {};
    const fulfillmentLocation = `${item.fulfillment_location ?? metafields.fulfillment_location ?? ''}`.trim();
    const shippingSeparately = parseBooleanFlag(item.shipping_separately ?? metafields.shipping_separately);
    const productId = `${item.varId ?? item.product_id ?? item.id ?? ''}`;
    const parentId = `${item.id ?? item.p_p_id ?? ''}`;
    const productTitle = item.title || item.product_title || 'Product';

    if (shippingSeparately) {
      const separateKey = `${parentId || productId || productTitle}`;
      const existing = separateShippingMap.get(separateKey);
      if (existing) {
        existing.qty += qty;
      } else {
        separateShippingMap.set(separateKey, {
          product_id: productId,
          p_p_id: parentId,
          product_title: productTitle,
          qty,
        });
      }
      return;
    }

    const box_width = toNumber(metafields.plant_base_width);
    const box_length = toNumber(metafields.plant_length);
    const box_height = toNumber(metafields.plant_height_in_cm_only || metafields.plant_height);
    const tray_quantity = toNumber(metafields.tray_quantity);

    if (!Number.isFinite(box_width) || box_width <= 0) return;
    if (!Number.isFinite(box_length) || box_length <= 0) return;
    if (!Number.isFinite(box_height) || box_height <= 0) return;
    if (!Number.isFinite(tray_quantity) || tray_quantity <= 0) return;

    const quantityRaw = qty / tray_quantity;
    if (!Number.isFinite(quantityRaw) || quantityRaw <= 0) return;

    trolleyItems.push({
      product_id: productId,
      p_p_id: parentId,
      qty,
      product_title: productTitle,
      box_width,
      box_length,
      box_height,
      quantity: quantityRaw,
      box_count: Math.ceil(quantityRaw),
      skipDeliverySync: !!fulfillmentLocation,
    });
  });

  return {
    trolleyItems,
    separateShippingItems: Array.from(separateShippingMap.values()).sort((a, b) => a.product_title.localeCompare(b.product_title)),
  };
}

function arrangeTrolleys(products, specs) {
  const { trolley_height, trolley_length, trolley_base_width } = specs;

  // ── 1. Build flat list of individual boxes ─────────────────────────────────
  const boxes = [];
  products.forEach((product) => {
    const repeat = Math.max(0, Math.floor(product.box_count));
    for (let i = 0; i < repeat; i += 1) {
      if (
        product.box_width  > trolley_length      ||
        product.box_height > trolley_height      ||
        product.box_length > trolley_base_width
      ) continue;
      boxes.push({
        product_id: product.product_id,
        p_p_id:     product.p_p_id,
        box_width:  product.box_width,
        box_length: product.box_length,
        box_height: product.box_height,
      });
    }
  });

  // ── 2. Sort: tallest first → largest floor-footprint first ─────────────────
  // Tallest items go to the bottom shelf; within the same height, bigger
  // footprints go first so they fill the 2D floor before smaller leftovers.
  boxes.sort((a, b) =>
    (b.box_height - a.box_height) ||
    ((b.box_width * b.box_length) - (a.box_width * a.box_length))
  );

  // ── 3. Bin-pack into trolleys / shelves (2D free-rectangle packing) ──────
  // Each shelf has a floor of trolley_length × trolley_base_width.
  // We maintain free rectangles so we can reuse internal gaps before opening
  // a new shelf, which improves both row and column utilization.
  const trolleys = [];
  let currentTrolley = { currentHeight: 0, shelves: [] };

  const newShelf = () => ({
    boxes: [],
    shelf_height: 0,
    freeRects: [{ x: 0, z: 0, w: trolley_length, d: trolley_base_width }],
  });
  let currentShelf = newShelf();

  const clamp = (n) => Math.round(n * 1000) / 1000;
  const minRectSide = 0.001;

  const pruneFreeRects = (rects) => {
    const kept = [];
    rects.forEach((r, i) => {
      if (r.w <= minRectSide || r.d <= minRectSide) return;
      const contained = rects.some((o, j) => {
        if (i === j) return false;
        return (
          r.x >= o.x &&
          r.z >= o.z &&
          r.x + r.w <= o.x + o.w &&
          r.z + r.d <= o.z + o.d
        );
      });
      if (!contained) kept.push(r);
    });
    return kept;
  };

  const splitFreeRect = (rect, placed) => {
    const parts = [];
    const rightW = clamp(rect.w - placed.w);
    const bottomD = clamp(rect.d - placed.d);

    // Guillotine split: produce non-overlapping free rects only.
    if (rightW > bottomD) {
      // Split vertically first: right strip + bottom-left strip.
      if (rightW > minRectSide) {
        parts.push({
          x: clamp(rect.x + placed.w),
          z: rect.z,
          w: rightW,
          d: rect.d,
        });
      }
      if (bottomD > minRectSide) {
        parts.push({
          x: rect.x,
          z: clamp(rect.z + placed.d),
          w: placed.w,
          d: bottomD,
        });
      }
    } else {
      // Split horizontally first: bottom strip + top-right strip.
      if (bottomD > minRectSide) {
        parts.push({
          x: rect.x,
          z: clamp(rect.z + placed.d),
          w: rect.w,
          d: bottomD,
        });
      }
      if (rightW > minRectSide) {
        parts.push({
          x: clamp(rect.x + placed.w),
          z: rect.z,
          w: rightW,
          d: placed.d,
        });
      }
    }

    return parts;
  };

  const overlaps2D = (a, b) => {
    const aRight = a.x + a.width;
    const aBack = a.z + a.length;
    const bRight = b.x + b.width;
    const bBack = b.z + b.length;
    return (
      a.x < bRight - minRectSide &&
      aRight > b.x + minRectSide &&
      a.z < bBack - minRectSide &&
      aBack > b.z + minRectSide
    );
  };

  const findBestPlacement = (shelf, box) => {
    let best = null;

    shelf.freeRects.forEach((rect, rectIdx) => {
      const orientations = [
        { w: box.box_width, d: box.box_length },
      ];

      if (Math.abs(box.box_width - box.box_length) > 0.001) {
        orientations.push({ w: box.box_length, d: box.box_width });
      }

      orientations.forEach((orient) => {
        if (orient.w > rect.w || orient.d > rect.d) return;

        const waste = (rect.w * rect.d) - (orient.w * orient.d);
        const shortSide = Math.min(rect.w - orient.w, rect.d - orient.d);
        const score = (waste * 1000) + shortSide;

        if (!best || score < best.score) {
          const candidate = {
            x: rect.x,
            z: rect.z,
            width: orient.w,
            length: orient.d,
          };
          const collides = shelf.boxes.some((existing) => overlaps2D(candidate, existing));
          if (collides) return;

          best = {
            rectIdx,
            score,
            x: rect.x,
            z: rect.z,
            width: orient.w,
            length: orient.d,
            height: box.box_height,
            product_id: box.product_id,
            p_p_id: box.p_p_id,
          };
        }
      });
    });

    return best;
  };

  const placeInShelf = (shelf, box) => {
    const placement = findBestPlacement(shelf, box);
    if (!placement) return false;

    const targetRect = shelf.freeRects[placement.rectIdx];
    shelf.freeRects.splice(placement.rectIdx, 1, ...splitFreeRect(targetRect, {
      w: placement.width,
      d: placement.length,
    }));
    shelf.freeRects = pruneFreeRects(shelf.freeRects);

    shelf.boxes.push({
      product_id: placement.product_id,
      p_p_id: placement.p_p_id,
      x: placement.x,
      z: placement.z,
      y: 0,
      width: placement.width,
      length: placement.length,
      height: placement.height,
    });
    shelf.shelf_height = Math.max(shelf.shelf_height, placement.height);
    return true;
  };

  const commitShelf = () => {
    if (!currentShelf.boxes.length) return;
    currentTrolley.shelves.push(currentShelf);
    currentTrolley.currentHeight += currentShelf.shelf_height;
    currentShelf = newShelf();
  };

  const commitTrolley = () => {
    if (!currentTrolley.shelves.length) return;
    trolleys.push(currentTrolley);
    currentTrolley = { currentHeight: 0, shelves: [] };
  };

  boxes.forEach((box) => {
    const bh = box.box_height;
    let placed = false;

    while (!placed) {
      // If the shelf is empty and the trolley doesn't have enough height, roll trolley
      if (!currentShelf.boxes.length && currentTrolley.currentHeight + bh > trolley_height) {
        commitTrolley();
      }

      if (placeInShelf(currentShelf, box)) {
        placed = true;
      } else {
        // 2D floor exhausted (including reusable holes) — commit shelf and retry
        commitShelf();
        if (currentTrolley.currentHeight + bh > trolley_height) {
          commitTrolley();
        }
      }
    }
  });

  commitShelf();
  commitTrolley();

  // ── 4. Return same format as before ────────────────────────────────────────
  return trolleys.map((t) => t.shelves.map((s) => s.boxes));
}

/* =============================================================================
   DELIVERY SKU SYNC — Box Delivery + Trolley Delivery auto-update

   Rules:
     • 1 trolley with < 17 boxes  → Box Delivery × boxCount
     • 1 trolley with ≥ 17 boxes  → Trolley Delivery × 1
     • 2+ trolleys                → Trolley Delivery × trolleyCount

   Strategy — one path, atomic:
     • Every user cart change flows through Spark's preCartUpdateListener.
     • We project the post-update cart, run the bin-packer, compute the
       required delivery-SKU deltas, and bundle them into the SAME
       spark.updateCart() call as the user's change.
       Result: one round-trip, one toast, no race, no debounce.
     • One-shot reconciliation on page load corrects any drift caused by
       admin edits, other devices, or sessions where this code didn't run.
   ============================================================================= */

const DELIVERY_SKUS = {
  BOX:     'courier-shipping',
  TROLLEY: 'CCTrolley1',
};
const DELIVERY_SKU_SET = new Set([DELIVERY_SKUS.BOX, DELIVERY_SKUS.TROLLEY]);
const BOX_DELIVERY_CAP = 17;

async function getLatestSparkCart() {
  if (typeof window.spark?.getCart === 'function') {
    return window.spark.getCart();
  }
  if (typeof window.spark?.getCartCache === 'function') {
    return window.spark.getCartCache();
  }
  return null;
}

function calculateDeliveryQuantities(trolleyLayout) {
  const totalTrolleys = trolleyLayout.length;
  if (totalTrolleys === 0) return { trolleyQty: 0, boxQty: 0 };

  if (totalTrolleys === 1) {
    const boxesInTrolley = trolleyLayout[0].reduce((sum, shelf) => sum + shelf.length, 0);
    if (boxesInTrolley < BOX_DELIVERY_CAP) {
      return { trolleyQty: 0, boxQty: boxesInTrolley };
    }
    return { trolleyQty: 1, boxQty: 0 };
  }

  return { trolleyQty: totalTrolleys, boxQty: 0 };
}

function getCustomAttributesObject(customAttributes = []) {
  return (customAttributes || []).reduce((acc, attribute) => {
    if (attribute?.key) acc[attribute.key] = attribute.value;
    return acc;
  }, {});
}

// SparkLayer's documented delta field on updateCart product entries is
// `adjustQuantity`. We also accept `absoluteQuantity` / `quantity` as
// absolute-set forms so the projection survives any internal normalization.
function resolvePendingDelta(product, currentQty) {
  if (product.adjustQuantity !== undefined && product.adjustQuantity !== null) {
    return toNumber(product.adjustQuantity);
  }
  if (product.absoluteQuantity !== undefined && product.absoluteQuantity !== null) {
    return toNumber(product.absoluteQuantity) - currentQty;
  }
  if (product.quantity !== undefined && product.quantity !== null) {
    return toNumber(product.quantity) - currentQty;
  }
  return 0;
}

// Project the post-update cart by applying pending updateInput to current
// items. Delivery SKUs are excluded — they are managed, never user-touched.
function projectPostUpdateCart(cart, updateInput) {
  const projected = new Map();
  const cleared = !!updateInput?.clearCart;

  if (!cleared) {
    (cart?.items || []).forEach((line) => {
      if (!line?.sku || DELIVERY_SKU_SET.has(line.sku)) return;
      projected.set(line.sku, {
        sku: line.sku,
        id: line.parentExternalId,
        varId: line.externalId,
        title: line.name,
        qty: line.quantity,
        metafields: getCustomAttributesObject(line.customAttributes),
      });
    });
  }

  (updateInput?.products || []).forEach((product) => {
    if (!product?.sku || DELIVERY_SKU_SET.has(product.sku)) return;

    const existing = projected.get(product.sku);
    const currentQty = toNumber(existing?.qty) || 0;
    const delta = resolvePendingDelta(product, currentQty);
    const nextQty = currentQty + delta;

    if (nextQty <= 0) {
      projected.delete(product.sku);
      return;
    }

    const mergedMeta = {
      ...(existing?.metafields || {}),
      ...getCustomAttributesObject(product.customAttributes),
    };

    projected.set(product.sku, {
      sku: product.sku,
      id: existing?.id || product.parentExternalId || product.parentProductId || product.sku,
      varId: existing?.varId || product.externalId || product.variantExternalId || product.sku,
      title: existing?.title || product.name || product.title || product.sku,
      qty: nextQty,
      metafields: mergedMeta,
    });
  });

  return Array.from(projected.values()).map((p) => ({
    id: p.id,
    varId: p.varId,
    title: p.title,
    qty: p.qty,
    shipping_separately: p.metafields.shipping_separately,
    fulfillment_location: p.metafields.fulfillment_location,
    metafields: p.metafields,
  }));
}

function computeTargetDeliveryFor(trolleyData) {
  const { trolleyItems } = fetchCartItems(trolleyData);
  const deliveryItems = trolleyItems.filter((item) => !item.skipDeliverySync);
  const layout = arrangeTrolleys(deliveryItems, trolleySpecs);
  return calculateDeliveryQuantities(layout);
}

function buildDeliveryDeltaProducts(target, currentItems) {
  const currentBoxQty     = currentItems.find((i) => i.sku === DELIVERY_SKUS.BOX)?.quantity     || 0;
  const currentTrolleyQty = currentItems.find((i) => i.sku === DELIVERY_SKUS.TROLLEY)?.quantity || 0;

  const products = [];
  const boxDelta     = target.boxQty     - currentBoxQty;
  const trolleyDelta = target.trolleyQty - currentTrolleyQty;

  if (boxDelta     !== 0) products.push({ sku: DELIVERY_SKUS.BOX,     adjustQuantity: boxDelta });
  if (trolleyDelta !== 0) products.push({ sku: DELIVERY_SKUS.TROLLEY, adjustQuantity: trolleyDelta });

  return products;
}

// Called from theme.liquid's preCartUpdateListener. Returns the delivery-SKU
// entries to bundle into the user's updateCart payload.
async function buildDeliveryProductsForSparkUpdate(updateInput) {
  if (updateInput?.clearCart) return [];

  const incomingProducts = updateInput?.products || [];
  // If this update is ONLY adjusting delivery SKUs, it's our own corrective
  // call — skip recomputation so we don't clobber the deliberate delta.
  if (incomingProducts.length && incomingProducts.every((p) => DELIVERY_SKU_SET.has(p.sku))) {
    return [];
  }

  const cart = await getLatestSparkCart();
  if (!cart) return [];

  const projected = projectPostUpdateCart(cart, updateInput);
  const target = computeTargetDeliveryFor(projected);
  return buildDeliveryDeltaProducts(target, cart.items || []);
}

// Wrap spark.updateCart so EVERY cart mutation (drawer edits, ATC,
// programmatic) bundles the delivery delta into the same network call as
// the user's change. preCartUpdateListener may not fire for drawer-internal
// updates; wrapping updateCart itself catches them all.
//
// Idempotent: safe to call multiple times (guarded by spark._trolleyPatched).
function patchSparkUpdateCart() {
  if (!window.spark || typeof window.spark.updateCart !== 'function') return false;
  if (window.spark._trolleyPatched) return true;

  const orig = window.spark.updateCart.bind(window.spark);
  window.spark._trolleyPatched = true;

  window.spark.updateCart = async function trolleyWrappedUpdateCart(payload, suppressToast) {
    try {
      const products = payload?.products || [];
      const allDelivery = products.length && products.every((p) => DELIVERY_SKU_SET.has(p.sku));
      const isClear = !!payload?.clearCart;

      // Skip injection for our own corrective calls and full-cart clears.
      if (!allDelivery && !isClear) {
        const deliveryProducts = await buildDeliveryProductsForSparkUpdate(payload);
        if (deliveryProducts.length) {
          payload.products = (payload.products || []).filter((p) => !DELIVERY_SKU_SET.has(p.sku));
          payload.products.push(...deliveryProducts);
        }
      }
    } catch (err) {
      console.warn('[Trolley] updateCart bundling failed:', err);
    }
    return orig(payload, suppressToast);
  };

  console.log('[Trolley] spark.updateCart patched — delivery SKUs bundle atomically');
  return true;
}

// Load-time drift reconciler. Fires ONCE on page load to fix any state that
// changed outside this session (admin edits, other devices). After load, the
// updateCart wrapper handles every mutation atomically, so this never runs
// again — no two-step updates.
let _reconcileInFlight = false;

async function reconcileDelivery() {
  if (_reconcileInFlight) return;
  if (typeof window.spark?.updateCart !== 'function') return;

  _reconcileInFlight = true;
  try {
    const cart = await getLatestSparkCart();
    if (!cart) return;

    const trolleyData = sparkCartToTrolleyData(cart);
    const target = computeTargetDeliveryFor(trolleyData);
    const products = buildDeliveryDeltaProducts(target, cart.items || []);
    if (!products.length) return;

    console.log('[Trolley] Load-time reconcile:', products);
    await window.spark.updateCart({ products }, true);
  } catch (err) {
    console.warn('[Trolley] Reconcile failed:', err);
  } finally {
    _reconcileInFlight = false;
  }
}

function moveTrolley(parent, index, action) {
  const wrappers = parent.querySelectorAll('.trolley-wrapper');
  const total = wrappers.length;
  if (!total || index < 0 || index >= total) return index;

  wrappers[index].classList.remove('active');

  let newIndex = index;
  if (action === 'next') newIndex = (index + 1) % total;
  if (action === 'prev') newIndex = (index - 1 + total) % total;

  wrappers[newIndex].classList.add('active');
  return newIndex;
}

function renderTrolleyGrid(layout, containerId, popup = false, isPopupIndex = false) {
  const { trolley_height, trolley_length, trolley_base_width } = trolleySpecs;
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn(`[Trolley] Container not found: ${containerId}`);
    return;
  }

  container.innerHTML = '';
  let stop = false;

  layout.forEach((trolleyShelves, trolleyIndex) => {
    if (stop) return;

    const maxBoxesInShelf = trolleyShelves.reduce((max, shelf) => Math.max(max, shelf.length), 0);

    const trolleyWrapper = document.createElement('div');
    const tContainer = document.createElement('div');

    if (popup) {
      trolleyWrapper.className = isPopupIndex ? 'trolley-wrapper active' : 'trolley-wrapper';
      trolleyWrapper.setAttribute('data-trolley-index', trolleyIndex);
      tContainer.className = 'tContainer';

      const tHeading = document.createElement('div');
      tHeading.className = 'trolley-header';

      const next = document.createElement('div');
      next.className = 'trolley--next trolley--navs';
      const prev = document.createElement('div');
      prev.className = 'trolley--prev trolley--navs';

      next.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="116.364 0 279.273 512.001" xml:space="preserve" width="20" height="20">
        <path style="fill:#000" d="M388.819 239.537 156.092 6.816c-9.087-9.089-23.824-9.089-32.912.002-9.087 9.089-9.087 23.824.002 32.912l216.27 216.266-216.273 216.276c-9.087 9.089-9.087 23.824.002 32.912A23.2 23.2 0 0 0 139.636 512a23.2 23.2 0 0 0 16.457-6.817L388.819 272.45a23.27 23.27 0 0 0 0-32.913"/>
      </svg>`;
      prev.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="145.45387268066406 -0.0007696151733398438 221.0918731689453 512.0007934570312" xml:space="preserve">
        <path style="fill:#000" d="M361.889 474.762 197.817 256.006 361.889 37.237c7.713-10.283 5.627-24.871-4.655-32.582-10.282-7.713-24.871-5.626-32.582 4.655L150.109 242.042a23.27 23.27 0 0 0 0 27.927l174.544 232.72c4.572 6.097 11.56 9.311 18.637 9.311 4.858 0 9.759-1.517 13.945-4.656 10.283-7.713 12.367-22.3 4.654-32.582"/>
      </svg>`;

      const title = document.createElement('h3');
      title.textContent = `Trolley ${trolleyIndex + 1}`;
      title.className = 'trolley-title';

      tHeading.appendChild(prev);
      tHeading.appendChild(title);
      tHeading.appendChild(next);
      if (!isPopupIndex) trolleyWrapper.appendChild(tHeading);
    }

    const table = document.createElement('div');
    table.setAttribute('data-trolley-index', trolleyIndex);
    table.className = 'table';
    table.style.setProperty('--t-height', `${trolley_height}px`);
    table.style.setProperty('--t-width', `${trolley_length}px`);
    table.style.setProperty('--t-base-width', `${trolley_base_width}px`);
    table.style.setProperty('--t-s-gap', `${Math.max(0, trolleyShelves.length - 1)}`);
    table.style.setProperty('--t-b-gap', `${Math.max(0, maxBoxesInShelf - 1)}`);
    table.style.setProperty('--t-b-length', `${Math.max(1, maxBoxesInShelf)}`);

    for (let i = trolleyShelves.length - 1; i >= 0; i -= 1) {
      const shelf = trolleyShelves[i].slice().sort((a, b) => a.x - b.x);

      const tr = document.createElement('div');
      const lastTrCls = i === 0 ? 'last-tr' : '';
      tr.className = trolleyShelves.length > 1 ? `parent-tr ${lastTrCls}` : 'parent-tr only-tr';
      tr.style.zIndex = String(2 + i);

      shelf.forEach((box, index) => {
        const colspan = getColspanByWidth(box.width);

        const td = document.createElement('div');
        const isFirst = index === 0;
        td.className = popup
          ? `td not-nested cube${isFirst ? ' first-cell' : ''}`
          : `td not-nested${isFirst ? ' first-cell' : ''}`;

        td.style.setProperty('--b-width', `${colspan * BASE_UNIT_SIZE}px`);
        td.style.setProperty('--b-height', `${box.height}px`);
        td.style.setProperty('--b-depth', `${box.length}px`);
        td.setAttribute('colspan', String(colspan));
        td.setAttribute('data-height', `${box.height}cm`);
        td.setAttribute('data-width', `${box.width}cm`);
        td.setAttribute('data-depth', `${box.length}cm`);
        td.setAttribute('data-id', box.product_id);

        if (popup) {
          ['box-f-2', 'box-f-2-opp', 'box-f-1-opp', 'box-f-1'].forEach((className) => {
            const face = document.createElement('div');
            face.className = className;
            td.appendChild(face);
          });
        }

        tr.appendChild(td);
      });

      table.appendChild(tr);
    }

    const wheels = document.createElement('div');
    wheels.className = 'wheels';

    for (let w = 0; w < 2; w += 1) {
      const wheel = document.createElement('div');
      wheel.className = 'wheel';
      wheels.appendChild(wheel);
    }

    table.appendChild(wheels);

    if (popup) {
      const overviewData = trolleyPopupOverview(trolleyShelves);
      tContainer.appendChild(table);
      trolleyWrapper.appendChild(tContainer);
      if (!isPopupIndex) trolleyWrapper.appendChild(overviewData);
      container.appendChild(trolleyWrapper);
    } else if (['trolleyContainer', 'MobTrolleyContainer'].includes(containerId) && trolleyIndex === 2 && layout.length > 3) {
      const counter = document.createElement('div');
      counter.className = 'numeric-plus-digits';
      counter.textContent = `+ ${layout.length - 2}`;
      container.appendChild(counter);
      stop = true;
    } else {
      container.appendChild(table);
    }
  });
}

// Stores last computed layout so slider can access it
let _lastTrolleyLayout = [];
let _lastSeparateShippingItems = [];
let _currentPopupView = 'default';

function buildTrolleyOverview(trolleyShelves) {
  const div = document.createElement('div');
  div.className = 'trolley-content--overview';
  const inner = document.createElement('div');
  inner.className = 'overview--container';

  const h = document.createElement('p');
  h.className = 'overview--heading';
  h.textContent = "What's on this trolley?";
  inner.appendChild(h);

  trolleyShelves.forEach((shelf, idx) => {
    const sh = document.createElement('p');
    sh.className = 'overview--sub-heading';
    sh.textContent = `Shelf ${idx + 1}`;
    inner.appendChild(sh);

    const counts = new Map();
    shelf.forEach((box) => {
      const key = String(box.p_p_id || box.product_id);
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    counts.forEach((boxCount, pid) => {
      const product = getProductMetaByParentId(pid);
      const title = product?.title || 'Product';
      const trayQty = toNumber(product?.metafields?.tray_quantity);
      const perTray = Number.isFinite(trayQty) && trayQty > 0 ? trayQty : 1;
      const row = document.createElement('p');
      row.className = 'overview--box-data';
      row.textContent = `${boxCount * perTray} x ${title}`;
      inner.appendChild(row);
    });
  });

  div.appendChild(inner);
  return div;
}

function buildSeparateShippingItems(items) {
  const fragment = document.createDocumentFragment();

  items.forEach((item) => {
    const row = document.createElement('p');
    row.className = 'overview--box-data';
    row.textContent = `${item.qty} x ${item.product_title}`;
    fragment.appendChild(row);
  });

  return fragment;
}

function refreshSliderOverview(idx) {
  const overviewEl = document.getElementById('trolley-slider-overview');
  if (!overviewEl) return;
  overviewEl.innerHTML = '';
  const slideData = _lastTrolleyLayout[idx];
  if (slideData) overviewEl.appendChild(buildTrolleyOverview(slideData));
}

function refreshSeparateShippingOverview() {
  const itemsEl = document.getElementById('trolley-separate-items');
  if (!itemsEl) return;
  itemsEl.innerHTML = '';
  if (_lastSeparateShippingItems.length) {
    itemsEl.appendChild(buildSeparateShippingItems(_lastSeparateShippingItems));
  }
}

function syncSeparateShippingButton() {
  const button = document.getElementById('show-separate-shipping-btn');
  if (!button) return;

  const hasSeparateShippingItems = _lastSeparateShippingItems.length > 0;
  button.hidden = !hasSeparateShippingItems;
  button.style.display = hasSeparateShippingItems ? '' : 'none';
  button.setAttribute('aria-hidden', hasSeparateShippingItems ? 'false' : 'true');
  button.disabled = !hasSeparateShippingItems;
}

function setPopupView(mode) {
  _currentPopupView = mode;

  const defaultView = document.getElementById('trolley-popup-default');
  const sliderView = document.getElementById('trolley-popup-slider');
  const sliderNav = document.getElementById('trolley-slider-nav');
  const separateView = document.getElementById('trolley-popup-separate');
  const separateNav = document.getElementById('trolley-separate-nav');

  if (defaultView) defaultView.style.display = mode === 'default' ? '' : 'none';
  if (sliderView) sliderView.style.display = mode === 'trolley-overview' ? '' : 'none';
  if (sliderNav) sliderNav.style.display = mode === 'trolley-overview' ? '' : 'none';
  if (separateView) separateView.style.display = mode === 'separate-shipping' ? '' : 'none';
  if (separateNav) separateNav.style.display = mode === 'separate-shipping' ? '' : 'none';
}

function showDefaultView() {
  setPopupView('default');

  try {
    build3DTrolleyScene('trolley-canvas-wrapper', _lastTrolleyLayout);
  } catch (err) {
    console.warn('[Trolley] showDefaultView 3D build failed:', err.message);
  }
}

function showSliderView(idx = 0) {
  setPopupView('trolley-overview');

  try {
    build3DTrolleyScene('trolley-slider-canvas-wrapper', _lastTrolleyLayout);
  } catch (err) {
    console.warn('[Trolley] showSliderView 3D build failed:', err.message);
  }

  const safeIndex = Math.min(Math.max(idx, 0), Math.max(_lastTrolleyLayout.length - 1, 0));
  const counter = document.getElementById('trolley-slide-counter');
  if (counter) counter.textContent = `${safeIndex + 1} / ${Math.max(_lastTrolleyLayout.length, 1)}`;

  refreshSliderOverview(safeIndex);
}

function showSeparateShippingView() {
  if (!_lastSeparateShippingItems.length) return;

  setPopupView('separate-shipping');
  refreshSeparateShippingOverview();
}

function trolleyInIt(data = []) {
  try {
    const { trolleyItems, separateShippingItems } = fetchCartItems(data);
    const trolleyLayout = arrangeTrolleys(trolleyItems, trolleySpecs);
    _lastTrolleyLayout = trolleyLayout;
    _lastSeparateShippingItems = separateShippingItems;

    ['trolleyContainer', 'MobTrolleyContainer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element) element.innerHTML = '';
    });

    renderTrolleyGrid(trolleyLayout, 'trolleyContainer');
    renderTrolleyGrid(trolleyLayout, 'MobTrolleyContainer');

    syncSeparateShippingButton();
    refreshSeparateShippingOverview();

    const prevBtn = document.getElementById('trolley-btn-prev');
    const nextBtn = document.getElementById('trolley-btn-next');
    const counter = document.getElementById('trolley-slide-counter');
    const multiTrolley = trolleyLayout.length > 1;
    if (prevBtn) prevBtn.style.display = multiTrolley ? '' : 'none';
    if (nextBtn) nextBtn.style.display = multiTrolley ? '' : 'none';
    if (counter) counter.style.display = multiTrolley ? '' : 'none';

    if (_currentPopupView === 'separate-shipping' && !_lastSeparateShippingItems.length) {
      showDefaultView();
    } else if (_currentPopupView === 'trolley-overview') {
      showSliderView(0);
    } else if (_currentPopupView === 'separate-shipping') {
      showSeparateShippingView();
    } else {
      showDefaultView();
    }

    console.log('[Trolley] Initialized with', trolleyLayout.length, 'trolleys and', _lastSeparateShippingItems.length, 'separate-shipping items');
  } catch (error) {
    console.error('[Trolley] Initialization error:', error);
  }
}

document.addEventListener('click', (event) => {
  const item = event.target;

  const inDesktop = item.closest('.table[data-trolley-index]') && item.closest('.header__icons');
  const inMobile  = item.closest('.table[data-trolley-index]') && item.closest('#MobTrolleyContainer');
  if (inDesktop || inMobile) {
    const popup = document.getElementById('trolley-popup');
    if (!popup) return;
    syncSeparateShippingButton();
    showDefaultView();
    popup.classList.add('active');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('trolley-popup-open');
  }

  if (item.closest('.trolley-popup--overlay') || item.closest('.trolley-popup--close')) {
    const popup = document.getElementById('trolley-popup');
    if (popup) popup.classList.remove('active');
    document.body.style.overflow = '';
    document.body.classList.remove('trolley-popup-open');
  }

  if (item.closest('#show-trolley-btn')) {
    if (_lastTrolleyLayout && _lastTrolleyLayout.length) showSliderView(0);
  }

  if (item.closest('#show-separate-shipping-btn')) {
    showSeparateShippingView();
  }

  if (item.closest('#back-to-default-btn') || item.closest('#back-from-separate-btn')) {
    showDefaultView();
  }
});

// Expose as global so SparkLayer's inline <script> can call trolleyInIt(...)
window.trolleyInIt = trolleyInIt;
if (Array.isArray(window._pendingTrolleyProductMeta)) {
  const pendingTrolleyProductMeta = window._pendingTrolleyProductMeta;
  window._pendingTrolleyProductMeta = null;
  trolleyInIt(pendingTrolleyProductMeta);
}

function sparkCartToTrolleyData(cart) {
  const items = cart?.items || [];
  return items
    .filter((line) => line.quantity > 0 && !DELIVERY_SKU_SET.has(line.sku))
    .map((line) => {
      const metafields = (line.customAttributes || []).reduce((acc, a) => {
        acc[a.key] = a.value;
        return acc;
      }, {});
      return {
        id: line.parentExternalId,
        varId: line.externalId,
        title: line.name,
        qty: line.quantity,
        shipping_separately: metafields.shipping_separately,
        metafields,
      };
    });
}

window.trolleyBuildDeliveryProductsForSparkUpdate = buildDeliveryProductsForSparkUpdate;
window.trolleyReconcileDelivery = reconcileDelivery;
window.trolleyPatchSparkUpdateCart = patchSparkUpdateCart;
window.spark = window.spark || {};
window.spark.oncartload = (cart) => trolleyInIt(sparkCartToTrolleyData(cart));
window.spark.oncartupdate = (cart) => {
  trolleyInIt(sparkCartToTrolleyData(cart));
  // Spark's drawer (+/-, remove, manual qty edit) updates the cart through an
  // internal path that bypasses both preCartUpdateListener and our updateCart
  // wrapper. The only hook we get is oncartupdate — fire reconcile here so
  // delivery SKUs converge to the correct quantity. For ATC the wrapper has
  // already bundled the delivery delta into the same call, so reconcile here
  // is a no-op (no second network round-trip).
  reconcileDelivery();
};

// Best-effort immediate patch; if Spark isn't ready yet, onReady will retry.
patchSparkUpdateCart();

if (window._pendingTrolleyPatch) {
  window._pendingTrolleyPatch = false;
  patchSparkUpdateCart();
}
if (window._pendingTrolleyReconcile) {
  window._pendingTrolleyReconcile = false;
  reconcileDelivery();
}



/* =============================================================================
   THREE.JS 3D TROLLEY RENDERER
   Three.js and OrbitControls are imported at the top of this ES module.
   build3DTrolleyScene() is called from trolleyInIt().
   ============================================================================= */

(function () {
  // Cardboard-tone palette — kraft browns / manila tans per product
  const BOX_PALETTE = [
    0xC4956A, // kraft brown
    0xD4A96A, // warm tan
    0xBF8C5A, // dark kraft
    0xE0B87A, // manila yellow
    0xC8A060, // mid-tan
    0xD2956A, // terracotta tan
    0xB87F50, // deep brown
    0xDDB87A, // light cardboard
    0xCA9055, // warm umber
    0xBCA060, // olive tan
  ];

  // Trolley physical specs (cm) - must match trolleySpecs
  const T = { h: 190, w: 127, d: 58, post: 3, shelf: 2, wheel: 12 };

  // Scene state
  let scene, camera, renderer, controls, animFrameId;
  let activeTrolleyGroup = null;
  let currentLayout = [];
  let currentSlideIdx = 0;

  // Build trolley frame meshes
  function buildTrolleyFrame() {
    const grp = new THREE.Group();
    const ox = -T.w / 2, oy = 0, oz = -T.d / 2;

    // Flat silver frame (realistic metal)
    const matFrame = new THREE.MeshStandardMaterial({
      color: 0xcccccc,      // realistic light silver
      roughness: 0.35,      // enough roughness to scatter light realistically
      metalness: 0.50,      // realistic metalness for galvanized steel
    });
    
    // Slotted post frame (Danish trolley has vertical slots on the pillars)
    const cw = 32, ch = 64;
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#cccccc'; // Match matFrame color
    ctx.fillRect(0, 0, cw, ch);
    // Draw the slot (dark hole in the vertical middle)
    ctx.fillStyle = '#111111';
    ctx.beginPath();
    ctx.roundRect(cw / 2 - 2, 4, 4, ch - 8, 2);
    ctx.fill();
    const slotTex = new THREE.CanvasTexture(canvas);
    slotTex.wrapS = THREE.RepeatWrapping;
    slotTex.wrapT = THREE.RepeatWrapping;
    
    const matFramePosts = new THREE.MeshStandardMaterial({
      map: slotTex,
      roughness: 0.35,
      metalness: 0.50,
    });
    // Flat silver tray
    const matShelf = new THREE.MeshStandardMaterial({
      color: 0xbdbdbd,      // slightly darker for the flat tray
      roughness: 0.40,
      metalness: 0.45,
    });
    // Danish trolley polyurethane outer tire (custom color)
    const matWheel = new THREE.MeshStandardMaterial({
      color: 0xB29159,
      roughness: 0.85,
      metalness: 0.1,
    });
    // Danish trolley inner hub (gray)
    const matHub = new THREE.MeshStandardMaterial({
      color: 0x999999,
      roughness: 0.50,
      metalness: 0.50,
    });

    // 4 corner posts (longer than normal, typical for Danish trolleys, no top rails)
    const postGeo = new THREE.BoxGeometry(T.post, T.h, T.post);
    // Adjust UV map repeating so the slots tile vertically based on height
    const repeatY = Math.max(1, Math.floor(T.h / 5)); // A slot every 5cm
    slotTex.repeat.set(1, repeatY);

    [[0, 0], [T.w, 0], [0, T.d], [T.w, T.d]].forEach(([px, pz]) => {
      const m = new THREE.Mesh(postGeo, matFramePosts);
      m.position.set(ox + px, oy + T.h / 2, oz + pz);
      m.castShadow = true;
      grp.add(m);
    });

    // Thicker, load-bearing base plate (dropped down slightly to prevent intersecting with bottom products)
    const baseThick = T.post * 1.5;
    const baseGeo = new THREE.BoxGeometry(T.w + T.post, baseThick, T.d + T.post);
    const base = new THREE.Mesh(baseGeo, matFrame);
    base.position.set(0, -baseThick / 2, 0); // Sits entirely below Y=0 so products resting at Y=0 are safe
    base.castShadow = true;
    grp.add(base);

    // 4 Independent casters (swivel brackets + wheels)
    const casterBracketHeight = 8;
    const bracketGeoTop = new THREE.BoxGeometry(T.post * 1.5, 2, T.post * 1.5);
    const bracketGeoSide = new THREE.BoxGeometry(2, casterBracketHeight, T.post * 1.5);
    
    // Using Torus for the outer tire to give it a realistic rounded profile
    const tireRadius = T.wheel / 2 - 1.5;
    const tireTube = 1.5;
    const whGeo = new THREE.TorusGeometry(tireRadius, tireTube, 16, 32);
    
    [[0, 0], [T.w, 0], [0, T.d], [T.w, T.d]].forEach(([px, pz]) => {
      // Bracket Fork Group
      const forkGrp = new THREE.Group();
      
      const topPlate = new THREE.Mesh(bracketGeoTop, matFrame);
      topPlate.position.set(0, casterBracketHeight / 2 - 1, 0);
      forkGrp.add(topPlate);

      // Two side prongs for the caster fork
      const leftProng = new THREE.Mesh(bracketGeoSide, matFrame);
      leftProng.position.set(-2, 0, 0);
      forkGrp.add(leftProng);
      
      const rightProng = new THREE.Mesh(bracketGeoSide, matFrame);
      rightProng.position.set(2, 0, 0);
      forkGrp.add(rightProng);
      
      forkGrp.position.set(ox + px, -baseThick - casterBracketHeight / 2, oz + pz);
      grp.add(forkGrp);

      // Wheel Group (Tire + Hub)
      const whGrp = new THREE.Group();

      // Outer Tire
      const whTire = new THREE.Mesh(whGeo, matWheel);
      whGrp.add(whTire);

      // Inner Hub (fits inside the torus)
      const hubGeo = new THREE.CylinderGeometry(tireRadius, tireRadius, tireTube * 2.5, 28);
      const whHub = new THREE.Mesh(hubGeo, matHub);
      whHub.rotation.x = Math.PI / 2; // align cylinder with torus
      whGrp.add(whHub);

      // Axle ping connecting the hub to the fork
      const pinGeo = new THREE.CylinderGeometry(1, 1, 6, 12);
      const pin = new THREE.Mesh(pinGeo, matFrame);
      pin.rotation.x = Math.PI / 2;
      whGrp.add(pin);

      whGrp.rotation.z = Math.PI / 2;
      whGrp.position.set(ox + px, -baseThick - casterBracketHeight - T.wheel / 2, oz + pz);
      grp.add(whGrp);
    });

    // Notice: Removed top rails, handles, side rails, and long wheel axles 
    // to match the open-post, individual caster design of Danish trolleys.

    return { grp, matShelf };
  }

  // Shelf boards at correct heights
  function buildShelfBoards(matShelf, trolleyShelves) {
    const grp = new THREE.Group();
    const shGeo = new THREE.BoxGeometry(T.w, T.shelf, T.d);
    let usedHeight = 0;

    trolleyShelves.forEach((shelf, idx) => {
      const shelfHeight = shelf.reduce((m, b) => Math.max(m, b.height), 0);
      if (idx > 0) {
        // Board sits between previous shelf's content and next shelf's content.
        // BoxGeometry origin is at center, so position at usedHeight + T.shelf/2.
        const sh = new THREE.Mesh(shGeo, matShelf);
        sh.position.set(0, usedHeight + T.shelf / 2, 0);
        sh.receiveShadow = true;
        sh.castShadow = true;
        grp.add(sh);
      }
      usedHeight += shelfHeight;
    });

    return grp;
  }  


  // Product boxes from real metafield dimensions 
  function buildTrolleyContents(trolleyShelves) {
    const grp = new THREE.Group();
    const ox = -T.w / 2, oz = -T.d / 2;
    const productColorMap = {};
    let paletteIdx = 0;
    let usedHeight = 0; // floor Y of current shelf's content area

    trolleyShelves.forEach((shelf, shelfIdx) => {
      // Boxes on shelf 0 sit directly on base (usedHeight=0).
      // Boxes on shelf 1+ sit on top of the physical shelf board.
      const floorY = usedHeight + (shelfIdx > 0 ? T.shelf : 0);
      const shelfHeight = shelf.reduce((m, b) => Math.max(m, b.height), 0);

      shelf.forEach((box) => {
        const pid = box.p_p_id || box.product_id || 'unknown';
        if (!(pid in productColorMap)) {
          productColorMap[pid] = BOX_PALETTE[paletteIdx % BOX_PALETTE.length];
          paletteIdx++;
        }
        const boxColor = productColorMap[pid];

        const w = Math.max(1, box.width);
        const h = Math.max(1, box.height);
        const d = Math.max(1, box.length);

        // Use explicit z from packer (front-to-back row) or centre in depth (fallback)
        const boxZ = typeof box.z === 'number' ? box.z : (T.d - d) / 2;

        // Cardboard material — matte, warm, zero metalness
        const cardboardColor = boxColor;
        const geo = new THREE.BoxGeometry(w - 0.5, h - 0.5, d - 0.5);
        const mat = new THREE.MeshStandardMaterial({
          color: cardboardColor,
          roughness: 0.92,   // very matte, like real corrugated cardboard
          metalness: 0.0,
        });
        const mesh = new THREE.Mesh(geo, mat);
        // X: left edge of trolley frame + box's X offset + half box width
        // Y: shelf floor + half box height
        // Z: front of trolley + box's Z offset + half box length
        mesh.position.set(
          ox + box.x + w / 2,
          floorY + h / 2,
          oz + boxZ + d / 2
        );
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        grp.add(mesh);

        // Dark cardboard-flap edge lines
        const lines = new THREE.LineSegments(
          new THREE.EdgesGeometry(geo),
          new THREE.LineBasicMaterial({
            color: new THREE.Color(cardboardColor).multiplyScalar(0.50),
            transparent: true,
            opacity: 0.70,
          })
        );
        lines.position.copy(mesh.position);
        grp.add(lines);
      });

      // Advance floor tracker: this shelf's content height
      usedHeight += shelfHeight;
    });

    return grp;
  }


  // â”€â”€ Render one slide â”€
  function renderSlide(idx) {
    if (!scene) return;
    if (activeTrolleyGroup) {
      scene.remove(activeTrolleyGroup);
      activeTrolleyGroup = null;
    }

    const slideData = currentLayout[idx];
    if (!slideData) { updateSlideUI(idx); return; }

    const slideGroup = new THREE.Group();
    // Move the trolley up so the wheels rest exactly on the shadow floor plane
    const baseThick = T.post * 1.5;
    const casterBracketHeight = 8;
    slideGroup.position.y = baseThick + casterBracketHeight + T.wheel;

    const { grp: frameGrp, matShelf } = buildTrolleyFrame();
    slideGroup.add(frameGrp);
    slideGroup.add(buildShelfBoards(matShelf, slideData));
    slideGroup.add(buildTrolleyContents(slideData));

    activeTrolleyGroup = slideGroup;
    scene.add(slideGroup);
    updateSlideUI(idx);
  }

  // UI helpers
  function updateSlideUI(idx) {
    const counter = document.getElementById('trolley-slide-counter');
    const btnPrev = document.getElementById('trolley-btn-prev');
    const btnNext = document.getElementById('trolley-btn-next');
    const total = currentLayout.length || 1;
    if (counter) counter.textContent = `${idx + 1} / ${total}`;
    if (btnPrev) btnPrev.disabled = idx === 0;
    if (btnNext) btnNext.disabled = idx >= currentLayout.length - 1;
  }

  // ── WebGL availability cache ────────────────────────────────────────────────
  // Tested once; null = untested, true = available, false = unavailable.
  let webGLAvailable = null;

  function checkWebGL() {
    if (webGLAvailable !== null) return webGLAvailable;
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      webGLAvailable = !!(ctx && ctx instanceof WebGLRenderingContext);
    } catch (e) {
      webGLAvailable = false;
    }
    return webGLAvailable;
  }

  function showWebGLFallback(wrapper) {
    wrapper.innerHTML = `
      <div class="trolley-3d-empty" style="flex-direction:column;gap:10px;">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="22" stroke="#bbb" stroke-width="2"/>
          <text x="24" y="30" text-anchor="middle" font-size="22" fill="#bbb">3D</text>
        </svg>
        <span style="font-size:1.3rem;color:#888;text-align:center;padding:0 1rem;">
          3D preview is not available on this device.<br>Please see the trolley overview on the right.
        </span>
      </div>`;
  }

  // Main build function
  function build3DTrolleyScene(containerId, layout) {
    const wrapper = document.getElementById(containerId);
    if (!wrapper) return;

    currentLayout = layout || [];
    currentSlideIdx = 0;

    if (!currentLayout.length) {
      wrapper.innerHTML = '<div class="trolley-3d-empty">Add products to see your trolley in 3D</div>';
      updateSlideUI(0);
      return;
    }

    // ── Graceful WebGL check — popup still opens even without GPU support ──
    if (!checkWebGL()) {
      showWebGLFallback(wrapper);
      updateSlideUI(0);
      console.warn('[Trolley] WebGL unavailable — showing fallback UI.');
      return;
    }

    // Destroy any existing renderer
    if (renderer) {
      if (animFrameId) cancelAnimationFrame(animFrameId);
      renderer.dispose();
      renderer.domElement?.remove();
      renderer = null; scene = null; camera = null; controls = null;
      activeTrolleyGroup = null;
    }
    wrapper.innerHTML = '';

    // Scene
    scene = new THREE.Scene();
    scene.background = null;

    // Camera
    const w = wrapper.clientWidth || 600;
    const h = wrapper.clientHeight || 420;
    camera = new THREE.PerspectiveCamera(34, w / h, 1, 4000);
    camera.position.set(300, 180, 300);

    // Renderer — wrapped in try-catch as a final safety net
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (err) {
      console.warn('[Trolley] WebGLRenderer failed:', err.message);
      webGLAvailable = false; // cache so next open skips the attempt
      showWebGLFallback(wrapper);
      updateSlideUI(0);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.display = 'block';
    wrapper.appendChild(renderer.domElement);

    // OrbitControls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.maxPolarAngle = Math.PI / 1.85;
    controls.minPolarAngle = Math.PI / 4;
    controls.target.set(0, T.h / 2 + T.wheel, 0);
    controls.update();

    // Lighting — tuned for realistic metal and realistic shadows
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));  // Neutral ambient to prevent "cartoon" washed out look

    // Primary key light (top-right-front) casting crisp shadows
    const key = new THREE.DirectionalLight(0xfff5e6, 1.5);
    key.position.set(150, 300, 200);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 0.5;
    key.shadow.camera.far = 1500;
    key.shadow.camera.left = -300;
    key.shadow.camera.right = 300;
    key.shadow.camera.top = 300;
    key.shadow.camera.bottom = -300;
    key.shadow.bias = -0.0005;
    scene.add(key);

    // Soft fill from left to give volume
    const fill = new THREE.DirectionalLight(0xdbe9f4, 0.6);
    fill.position.set(-200, 150, -100);
    scene.add(fill);

    // Rim light from behind-right — creates metallic edge highlight separating it from background
    const rim = new THREE.DirectionalLight(0xffffff, 0.8);
    rim.position.set(200, 100, -300);
    scene.add(rim);

    // Use physically correct lighting (replacement for deprecated useLegacyLights)
    renderer.physicallyCorrectLights = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Shadow floor
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(1200, 1200),
      new THREE.ShadowMaterial({ opacity: 0.06 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // First slide
    renderSlide(0);

    // Animate
    (function animate() {
      animFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    })();

    // Slide control (called by HTML button onclick)
    window.trolleyChangeSlide = (dir) => {
      const next = currentSlideIdx + dir;
      if (next >= 0 && next < currentLayout.length) {
        currentSlideIdx = next;
        renderSlide(currentSlideIdx);

        // Update the counter badge
        const counter = document.getElementById('trolley-slide-counter');
        if (counter) counter.textContent = `${currentSlideIdx + 1} / ${currentLayout.length}`;

        // Sync the overview panel to the newly active trolley
        if (typeof refreshSliderOverview === 'function') {
          refreshSliderOverview(currentSlideIdx);
        }
      }
    };

    // Responsive resize
    new ResizeObserver(() => {
      if (!renderer || !camera) return;
      const nw = wrapper.clientWidth;
      const nh = wrapper.clientHeight;
      if (nw < 1 || nh < 1) return;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    }).observe(wrapper);
  }

  // Expose for trolleyInIt()
  window.build3DTrolleyScene = build3DTrolleyScene;

})();





